import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
import { botPath } from '../../lib/botNav';
import { shares } from '../../lib/qualityView';
import { hubService, type IntentQuality } from '../../services/hub.service';
import { GateTuning } from './GateTuning';
import { ShadowReadiness } from './ShadowReadiness';

/**
 * Bot workspace → Intents → Quality (2026-10-05): how hub's no-LLM classify answered this bot — matched, handed on
 * (LLM / default reply), or taken as a control signal — per day, which control codes fired, the top unmatched texts,
 * and the gate tuning section. A misfiring control pattern or a drop in matches shows up here first.
 */
export function QualityTab({ botId, system }: { botId: string; system: 'chatbot' | 'chatbot_v2' }) {
  const { getToken } = useAuth();
  const [days, setDays] = useState(7);
  const [data, setData] = useState<IntentQuality | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await hubService.getIntentQuality(botId, system, days, getToken()));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    }
  }, [botId, system, days, getToken]);

  useEffect(() => {
    void load();
  }, [load]);


  const pct = data ? shares(data.totals) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-600">Last</span>
        {[7, 30].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`rounded border px-2 py-0.5 ${days === d ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white'}`}
          >
            {d} days
          </button>
        ))}
        {error && <span className="text-red-600">{error}</span>}
      </div>

      {data && pct && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Matched (no LLM)" value={data.totals.deflected} pct={pct.deflected} tone="text-green-700" />
            <Stat label="Handed on (LLM / default)" value={data.totals.abstained} pct={pct.abstained} tone="text-amber-700" />
            <Stat label="Control signals" value={data.totals.control} pct={pct.control} tone="text-blue-700" />
            <Stat label="Review backlog" value={data.reviewBacklog} tone="text-gray-800" />
          </div>

          {Object.keys(data.controlByCode).length > 0 && (
            <div className="text-sm">
              <div className="mb-1 font-medium">Control signals by type</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.controlByCode)
                  .sort(([, a], [, b]) => b - a)
                  .map(([code, n]) => (
                    <span key={code} className="rounded bg-blue-50 px-2 py-0.5 text-blue-800">
                      {code} · {n}
                    </span>
                  ))}
              </div>
            </div>
          )}

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-1">Day</th>
                <th>Matched</th>
                <th>Handed on</th>
                <th>Control</th>
              </tr>
            </thead>
            <tbody>
              {data.daily.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-2 text-gray-500">
                    No classified messages in this window.
                  </td>
                </tr>
              )}
              {data.daily.map((d) => (
                <tr key={d.day} className="border-b border-gray-100">
                  <td className="py-1">{d.day}</td>
                  <td>{d.deflected}</td>
                  <td>{d.abstained}</td>
                  <td>{d.control}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="text-sm">
            <div className="mb-1 flex items-center justify-between">
              <span className="font-medium">Top unmatched</span>
              <Link to={botPath({ system, botId }, 'review')} className="text-blue-600 hover:underline">
                Open Review →
              </Link>
            </div>
            {data.topUnmatched.length === 0 ? (
              <div className="text-gray-500">Nothing waiting in Review.</div>
            ) : (
              <ul className="space-y-1">
                {data.topUnmatched.map((u) => (
                  <li key={`${u.surface}:${u.text}`} className="flex justify-between gap-3 border-b border-gray-100 py-1">
                    <span>{u.text}</span>
                    <span className="whitespace-nowrap text-xs text-gray-500">
                      {u.surface ?? 'comment'} · ×{u.seenCount}
                      {u.topScore != null ? ` · ${Number(u.topScore).toFixed(2)}` : ''}
                      {u.candidateCodes?.[0] ? ` → ${u.candidateCodes[0]}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      <ShadowReadiness botId={botId} system={system} />

      <GateTuning botId={botId} system={system} />
    </div>
  );
}

function Stat({ label, value, pct, tone }: { label: string; value: number; pct?: number; tone: string }) {
  return (
    <div className="rounded border border-gray-200 p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-xl font-semibold ${tone}`}>
        {value}
        {pct != null && <span className="ml-1 text-sm font-normal text-gray-500">{pct}%</span>}
      </div>
    </div>
  );
}
